import { eachMonthOfInterval, format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { supabase } from '../lib/supabase';

export interface ReportRange { start: string; end: string }
export interface InstallmentRecord {
  id: string; amount: number; currency: string; status: string;
  due_date: string; payment_date: string | null; installment_number: number;
  students?: { first_name: string; last_name: string } | null;
  contracts?: { contract_number: string } | null;
  branches?: { name: string } | null;
}
export interface ExpenseRecord {
  id: string; amount: number; currency: string; payment_status: string;
  category: string; expense_date: string; title: string; description: string | null;
  branches?: { name: string } | null;
}
export type Converted<T> = T & { amountEur: number };
export const categoryLabels: Record<string, string> = {
  office: 'Ofis', salary: 'Maaş', marketing: 'Pazarlama', rent: 'Kira',
  utilities: 'Faturalar', supplies: 'Malzeme', travel: 'Seyahat', other: 'Diğer',
};
export const statusLabels: Record<string, string> = {
  paid: 'Ödendi', pending: 'Bekliyor', overdue: 'Gecikti', cancelled: 'İptal',
};
export const money = (value: number) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
}).format(value);
export const effectiveStatus = (item: InstallmentRecord, today: string) =>
  item.status === 'pending' && item.due_date.slice(0, 10) < today ? 'overdue' : item.status;

export function buildFinancialReport(
  income: Converted<InstallmentRecord>[], installments: Converted<InstallmentRecord>[],
  expenses: Converted<ExpenseRecord>[], range: ReportRange, today = format(new Date(), 'yyyy-MM-dd'),
) {
  const monthly = eachMonthOfInterval({ start: parseISO(range.start), end: parseISO(range.end) })
    .map(date => ({ key: format(date, 'yyyy-MM'), name: format(date, 'MMM yyyy', { locale: tr }), gelir: 0, gider: 0, kar: 0 }));
  const months = new Map(monthly.map(month => [month.key, month]));
  income.forEach(item => {
    const month = months.get(item.payment_date?.slice(0, 7) || '');
    if (month) month.gelir += item.amountEur;
  });
  const categories = new Map<string, { name: string; value: number; color: string }>();
  const colors = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#64748B'];
  expenses.filter(item => item.payment_status === 'paid').forEach(item => {
    const month = months.get(item.expense_date.slice(0, 7));
    if (month) month.gider += item.amountEur;
    const name = categoryLabels[item.category] || item.category || 'Diğer';
    const category = categories.get(name) || { name, value: 0, color: colors[categories.size % colors.length] };
    category.value += item.amountEur;
    categories.set(name, category);
  });
  monthly.forEach(month => { month.kar = month.gelir - month.gider; });
  const totalIncome = income.reduce((sum, item) => sum + item.amountEur, 0);
  const totalExpenses = expenses.filter(item => item.payment_status === 'paid').reduce((sum, item) => sum + item.amountEur, 0);
  const counts = { paid: 0, pending: 0, overdue: 0, cancelled: 0 };
  installments.forEach(item => {
    const status = effectiveStatus(item, today);
    if (status in counts) counts[status as keyof typeof counts]++;
  });
  return { range, today, income, installments, expenses, monthly, categories: Array.from(categories.values()).sort((a, b) => b.value - a.value),
    totalIncome, totalExpenses, netProfit: totalIncome - totalExpenses, counts,
    undatedPaid: installments.filter(item => item.status === 'paid' && !item.payment_date).length };
}
export type FinancialReport = ReturnType<typeof buildFinancialReport>;

// Supabase's default row limit must not truncate financial totals.
async function fetchAll<T>(page: (from: number, to: number) => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await page(from, from + 999);
    if (error) throw new Error(error.message);
    const batch = (data || []) as T[];
    rows.push(...batch);
    if (batch.length < 1000) return rows;
  }
}

export async function loadFinancialReport(range: ReportRange, branchId?: string) {
  const installmentFields = 'id, amount, currency, status, due_date, payment_date, installment_number, students(first_name,last_name), contracts(contract_number), branches(name)';
  const installmentPage = (column: string, from: number, to: number, paid = false) => {
    let query = supabase.from('installments').select(installmentFields)
      .gte(column, range.start).lte(column, range.end).order('id').range(from, to);
    if (branchId) query = query.eq('branch_id', branchId);
    if (paid) query = query.eq('status', 'paid');
    return query;
  };
  const expensePage = (from: number, to: number) => {
    let query = supabase.from('expenses')
      .select('id, amount, currency, payment_status, category, expense_date, title, description, branches(name)')
      .gte('expense_date', range.start).lte('expense_date', range.end).order('id').range(from, to);
    if (branchId) query = query.eq('branch_id', branchId);
    return query;
  };
  const [income, installments, expenses] = await Promise.all([
    fetchAll<InstallmentRecord>((from, to) => installmentPage('payment_date', from, to, true)),
    fetchAll<InstallmentRecord>((from, to) => installmentPage('due_date', from, to)),
    fetchAll<ExpenseRecord>(expensePage),
  ]);
  const currencies = new Set([...income, ...installments, ...expenses].map(item => item.currency || 'EUR'));
  const rates = new Map<string, number>([['EUR', 1]]);
  if (Array.from(currencies).some(currency => currency !== 'EUR')) {
    const { data, error } = await supabase.from('exchange_rates').select('from_currency,to_currency,rate');
    if (error) throw new Error('Döviz kurları alınamadı: ' + error.message);
    // Stored rates represent source-currency units per one target-currency unit.
    for (const rate of data || []) {
      const value = Number(rate.rate);
      if (!Number.isFinite(value) || value <= 0) continue;
      if (rate.to_currency === 'EUR') rates.set(rate.from_currency, value);
      else if (rate.from_currency === 'EUR') rates.set(rate.to_currency, 1 / value);
    }
  }
  const convert = <T extends { amount: number; currency: string }>(item: T): Converted<T> => {
    const currency = item.currency || 'EUR';
    const rate = rates.get(currency);
    if (!rate) throw new Error(`${currency}/EUR kuru tanımlı değil. Döviz kurlarını güncelleyip yeniden deneyin.`);
    const amount = Number(item.amount);
    if (!Number.isFinite(amount)) throw new Error('Geçersiz tutar içeren bir muhasebe kaydı bulundu.');
    return { ...item, amount, currency, amountEur: amount / rate };
  };
  return buildFinancialReport(income.map(convert), installments.map(convert), expenses.map(convert), range);
}
